import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/useAuth';
import {
  deleteUserSettings,
  getUserSettings,
  putUserSettings,
  type UserSettings,
} from '../api/settingsClient';

type LoadState = 'idle' | 'loading' | 'saving' | 'deleting';

/**
 * Settings page where signed-in users paste their Omnichannel Live Chat widget snippet.
 * The server extracts orgId/orgUrl/widgetId from it; both raw + parsed are returned on GET so
 * consumer apps can decide for themselves whether to use the parsed fields or the raw snippet.
 */
export function SettingsPage() {
  const { account, isSignedIn, acquireApiToken } = useAuth();
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState<UserSettings | null>(null);
  const [state, setState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);

  const buildAuthArgs = useCallback(async () => {
    const bearer = await acquireApiToken();
    if (!bearer || !account?.homeAccountId || !account?.username) return null;
    return { authBearer: bearer, homeAccountId: account.homeAccountId, username: account.username };
  }, [account, acquireApiToken]);

  useEffect(() => {
    if (!isSignedIn) return;
    let cancelled = false;
    (async () => {
      setState('loading');
      setError(null);
      try {
        const auth = await buildAuthArgs();
        if (!auth) return;
        const current = await getUserSettings(auth);
        if (cancelled) return;
        setSaved(current);
        setDraft(current?.widgetScript ?? '');
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      } finally {
        if (!cancelled) setState('idle');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isSignedIn, buildAuthArgs]);

  const save = useCallback(async () => {
    if (!draft.trim()) return;
    setState('saving');
    setError(null);
    try {
      const auth = await buildAuthArgs();
      if (!auth) throw new Error('Not signed in.');
      const result = await putUserSettings({ ...auth, widgetScript: draft });
      setSaved(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setState('idle');
    }
  }, [draft, buildAuthArgs]);

  const clear = useCallback(async () => {
    setState('deleting');
    setError(null);
    try {
      const auth = await buildAuthArgs();
      if (!auth) throw new Error('Not signed in.');
      await deleteUserSettings(auth);
      setSaved(null);
      setDraft('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setState('idle');
    }
  }, [buildAuthArgs]);

  if (!isSignedIn) {
    return (
      <div style={styles.page}>
        <h2>Settings</h2>
        <p>You need to sign in to manage your settings.</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <h2 style={{ marginTop: 0 }}>Live chat widget</h2>
      <p style={styles.lead}>
        Paste the embed snippet from <strong>Customer Service admin center → Workstreams → your
        live chat widget → Code snippet</strong>. The server extracts <code>data-org-id</code>,{' '}
        <code>data-org-url</code>, and <code>data-app-id</code> from it.
      </p>

      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder='<script id="Microsoft_Omnichannel_LCWidget" ... data-org-id="..." data-org-url="..." data-app-id="..."></script>'
        spellCheck={false}
        rows={10}
        style={styles.textarea}
        disabled={state !== 'idle'}
      />

      <div style={styles.actions}>
        <button onClick={() => void save()} disabled={state !== 'idle' || !draft.trim()} style={styles.primary}>
          {state === 'saving' ? 'Saving…' : 'Save'}
        </button>
        {saved && (
          <button onClick={() => void clear()} disabled={state !== 'idle'} style={styles.danger}>
            {state === 'deleting' ? 'Removing…' : 'Remove'}
          </button>
        )}
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {saved && (
        <div style={styles.parsed}>
          <h3 style={{ marginTop: 0 }}>Parsed</h3>
          <dl style={styles.dl}>
            <dt>Organization ID</dt><dd><code>{saved.orgId}</code></dd>
            <dt>Organization URL</dt><dd><code>{saved.orgUrl}</code></dd>
            <dt>Widget ID</dt><dd><code>{saved.widgetId}</code></dd>
            <dt>Last updated</dt><dd>{new Date(saved.updatedUtc).toLocaleString()}</dd>
          </dl>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 760, margin: '0 auto', padding: '24px 24px 80px' },
  lead: { color: '#4b5563', fontSize: 14, lineHeight: 1.5 },
  textarea: {
    width: '100%',
    fontFamily: 'Consolas, Menlo, monospace',
    fontSize: 13,
    padding: 10,
    border: '1px solid #d1d5db',
    borderRadius: 4,
    resize: 'vertical',
  },
  actions: { display: 'flex', gap: 10, marginTop: 12 },
  primary: {
    background: '#0078d4',
    color: 'white',
    border: 'none',
    padding: '8px 18px',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 500,
  },
  danger: {
    background: 'transparent',
    color: '#b91c1c',
    border: '1px solid #b91c1c',
    padding: '8px 18px',
    borderRadius: 4,
    cursor: 'pointer',
    fontWeight: 500,
  },
  error: {
    marginTop: 12,
    padding: 10,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b',
    borderRadius: 4,
    fontSize: 13,
  },
  parsed: {
    marginTop: 24,
    padding: 16,
    background: '#f9fafb',
    border: '1px solid #e5e7eb',
    borderRadius: 6,
  },
  dl: { display: 'grid', gridTemplateColumns: '180px 1fr', rowGap: 6, columnGap: 12, margin: 0, fontSize: 13 },
};
